import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
const API = process.env.REACT_APP_BACKEND_URL + "/api/procurement";

export default function Categories() {
  const navigate = useNavigate();
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [newMainCategory, setNewMainCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState({
    name: '',
    main_category_id: '',
    main_category_name: ''
  });
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    try {
      const [mainRes, subRes] = await Promise.all([
        axios.get(`${API}/main-categories`),
        axios.get(`${API}/sub-categories`)
      ]);
      setMainCategories(mainRes.data);
      setSubCategories(subRes.data);
    } catch (error) {
      toast.error('خطأ في جلب البيانات');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddMain = async () => {
    if (!newMainCategory.trim()) {
      toast.error('الرجاء إدخال اسم التصنيف');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/main-categories`, { name: newMainCategory });
      toast.success('تم إضافة التصنيف الرئيسي بنجاح');
      setNewMainCategory('');
      fetchData();
    } catch (error) {
      toast.error('خطأ في إضافة التصنيف');
    }
    setLoading(false);
  };

  const handleAddSub = async () => {
    if (!newSubCategory.name.trim() || !newSubCategory.main_category_id) {
      toast.error('الرجاء إدخال اسم التصنيف الفرعي واختيار التصنيف الرئيسي');
      return;
    }

    setLoading(true);
    try {
      const selectedMain = mainCategories.find(c => c.id === newSubCategory.main_category_id);
      await axios.post(`${API}/sub-categories`, {
        ...newSubCategory,
        main_category_name: selectedMain.name
      });
      toast.success('تم إضافة التصنيف الفرعي بنجاح');
      setNewSubCategory({ name: '', main_category_id: '', main_category_name: '' });
      fetchData();
    } catch (error) {
      toast.error('خطأ في إضافة التصنيف الفرعي');
    }
    setLoading(false);
  };

  const handleDeleteMain = async (id) => {
    try {
      await axios.delete(`${API}/main-categories/${id}`);
      toast.success('تم حذف التصنيف الرئيسي بنجاح');
      fetchData();
    } catch (error) {
      toast.error('خطأ في حذف التصنيف');
    }
  };

  const handleDeleteSub = async (id) => {
    try {
      await axios.delete(`${API}/sub-categories/${id}`);
      toast.success('تم حذف التصنيف الفرعي بنجاح');
      fetchData();
    } catch (error) {
      toast.error('خطأ في حذف التصنيف');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-6">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2" style={{fontFamily: 'Manrope'}}>
              إدارة التصنيفات
            </h1>
            <p className="text-slate-600">أضف وأدر التصنيفات الرئيسية والفرعية</p>
          </div>
          <Button 
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/procurement')} 
            variant="outline"
            className="gap-2"
          >
            <ArrowLeft className="w-5 h-5 shrink-0" />
            <span>العودة</span>
          </Button>
        </div>

        <Tabs defaultValue="main" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="main" data-testid="main-categories-tab">التصنيفات الرئيسية</TabsTrigger>
            <TabsTrigger value="sub" data-testid="sub-categories-tab">التصنيفات الفرعية</TabsTrigger>
          </TabsList>

          <TabsContent value="main">
            <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
              <CardHeader>
                <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>إضافة تصنيف رئيسي جديد</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="main-category-name">اسم التصنيف الرئيسي</Label>
                    <Input
                      id="main-category-name"
                      data-testid="main-category-name-input"
                      placeholder="مثال: مواد بناء، تشطيبات، كهرباء"
                      value={newMainCategory}
                      onChange={(e) => setNewMainCategory(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddMain()}
                      className="mt-2"
                    />
                  </div>
                  <Button 
                    data-testid="add-main-category-btn"
                    onClick={handleAddMain} 
                    disabled={loading}
                    className="mt-8 bg-gradient-to-r from-blue-500 to-blue-600 hover:shadow-lg"
                  >
                    <Plus className="w-4 h-4 ml-2" />
                    إضافة
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/90">
              <CardHeader>
                <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>قائمة التصنيفات الرئيسية</CardTitle>
              </CardHeader>
              <CardContent>
                {mainCategories.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">لا توجد تصنيفات. قم بإضافة تصنيف جديد</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {mainCategories.map((category) => (
                      <div
                        key={category.id}
                        data-testid={`main-category-item-${category.id}`}
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg hover:shadow-md transition-shadow"
                      >
                        <span className="font-medium text-slate-800">{category.name}</span>
                        <Button
                          data-testid={`delete-main-category-${category.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMain(category.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sub">
            <Card className="mb-8 border-0 shadow-xl backdrop-blur-sm bg-white/90">
              <CardHeader>
                <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>إضافة تصنيف فرعي جديد</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>التصنيف الرئيسي</Label>
                    <Select 
                      value={newSubCategory.main_category_id} 
                      onValueChange={(value) => setNewSubCategory({...newSubCategory, main_category_id: value})}
                    >
                      <SelectTrigger data-testid="sub-main-category-select" className="mt-2">
                        <SelectValue placeholder="اختر التصنيف الرئيسي" />
                      </SelectTrigger>
                      <SelectContent position="popper">
                        {mainCategories.map(category => (
                          <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="sub-category-name">اسم التصنيف الفرعي</Label>
                    <Input
                      id="sub-category-name"
                      data-testid="sub-category-name-input"
                      placeholder="مثال: أسمنت، حديد، بلاط"
                      value={newSubCategory.name}
                      onChange={(e) => setNewSubCategory({...newSubCategory, name: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <Button 
                    data-testid="add-sub-category-btn"
                    onClick={handleAddSub} 
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-indigo-500 to-indigo-600 hover:shadow-lg"
                  >
                    <Plus className="w-4 h-4 ml-2" />
                    إضافة التصنيف الفرعي
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/90">
              <CardHeader>
                <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>قائمة التصنيفات الفرعية</CardTitle>
              </CardHeader>
              <CardContent>
                {subCategories.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">لا توجد تصنيفات فرعية. قم بإضافة تصنيف فرعي جديد</p>
                ) : (
                  <div className="space-y-3">
                    {subCategories.map((category) => (
                      <div
                        key={category.id}
                        data-testid={`sub-category-item-${category.id}`}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-slate-800">{category.name}</p>
                          <p className="text-sm text-slate-600 mt-1">
                            التصنيف الرئيسي: <span className="font-medium">{category.main_category_name}</span>
                          </p>
                        </div>
                        <Button
                          data-testid={`delete-sub-category-${category.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSub(category.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
